// Base language enum with all available languages
export enum LANGUAGES {
  AFRIKAANS = "afrikaans",
  AMHARIC = "amharic",
  ARABIC = "arabic",
  BULGARIAN = "bulgarian",
  BENGALI = "bengali",
  BOSNIAN = "bosnian",
  CZECH = "czech",
  WELSH = "welsh",
  DANISH = "danish",
  GERMAN = "german",
  GREEK = "greek",
  ENGLISH = "english",
  SPANISH = "spanish",
  ESTONIAN = "estonian",
  BASQUE = "basque",
  PERSIAN = "persian", // Farsi
  FINNISH = "finnish",
  FRENCH = "french",
  IRISH = "irish", // Irish Gaelic
  GAELIC = "gaelic", // Scottish Gaelic
  GUJARATI = "gujarati",
  HAUSA = "hausa",
  HEBREW = "hebrew",
  HINDI = "hindi",
  CROATIAN = "croatian",
  HUNGARIAN = "hungarian",
  INDONESIAN = "indonesian",
  IGBO = "igbo",
  ICELANDIC = "icelandic",
  ITALIAN = "italian",
  JAPANESE = "japanese",
  KAZAKH = "kazakh",
  KOREAN = "korean",
  KYRGYZ = "kyrgyz",
  LITHUANIAN = "lithuanian",
  LATVIAN = "latvian",
  MACEDONIAN = "macedonian",
  MARATHI = "marathi",
  MALAY = "malay",
  NEPALI = "nepali",
  DUTCH = "dutch",
  NORWEGIAN = "norwegian",
  PUNJABI = "punjabi",
  POLISH = "polish",
  PORTUGUESE = "portuguese",
  ROMANIAN = "romanian",
  RUSSIAN = "russian",
  SLOVAK = "slovak",
  SLOVENIAN = "slovenian",
  ALBANIAN = "albanian",
  SERBIAN = "serbian",
  SWEDISH = "swedish",
  SWAHILI = "swahili",
  TAMIL = "tamil",
  TELUGU = "telugu",
  TAJIK = "tajik",
  THAI = "thai",
  TAGALOG = "tagalog",
  TURKISH = "turkish",
  URDU = "urdu",
  UZBEK = "uzbek",
  VIETNAMESE = "vietnamese",
  YORUBA = "yoruba",
  CHINESE = "chinese",
  CHINESE_SIMPLIFIED = "chinese_simplified",
  CHINESE_TRADITIONAL = "chinese_traditional",
  // Legacy entries for backwards compatibility
  MANDARIN = "mandarin",
  OTHER = "other",
}

// Display languages - for UI translations (complete set based on available locales)
export const DISPLAY_LANGUAGES = {
  [LANGUAGES.AFRIKAANS]: "Afrikaans",
  [LANGUAGES.AMHARIC]: "Amharic",
  [LANGUAGES.ARABIC]: "Arabic",
  [LANGUAGES.BULGARIAN]: "Bulgarian",
  [LANGUAGES.BENGALI]: "Bengali",
  [LANGUAGES.BOSNIAN]: "Bosnian",
  [LANGUAGES.CZECH]: "Czech",
  [LANGUAGES.WELSH]: "Welsh",
  [LANGUAGES.DANISH]: "Danish",
  [LANGUAGES.GERMAN]: "German",
  [LANGUAGES.GREEK]: "Greek",
  [LANGUAGES.ENGLISH]: "English",
  [LANGUAGES.SPANISH]: "Spanish",
  [LANGUAGES.ESTONIAN]: "Estonian",
  [LANGUAGES.BASQUE]: "Basque",
  [LANGUAGES.PERSIAN]: "Farsi",
  [LANGUAGES.FINNISH]: "Finnish",
  [LANGUAGES.FRENCH]: "French",
  [LANGUAGES.IRISH]: "Irish",
  [LANGUAGES.GAELIC]: "Scottish Gaelic",
  [LANGUAGES.GUJARATI]: "Gujarati",
  [LANGUAGES.HAUSA]: "Hausa",
  [LANGUAGES.HEBREW]: "Hebrew",
  [LANGUAGES.HINDI]: "Hindi",
  [LANGUAGES.CROATIAN]: "Croatian",
  [LANGUAGES.HUNGARIAN]: "Hungarian",
  [LANGUAGES.INDONESIAN]: "Indonesian",
  [LANGUAGES.IGBO]: "Igbo",
  [LANGUAGES.ICELANDIC]: "Icelandic",
  [LANGUAGES.ITALIAN]: "Italian",
  [LANGUAGES.JAPANESE]: "Japanese",
  [LANGUAGES.KAZAKH]: "Kazakh",
  [LANGUAGES.KOREAN]: "Korean",
  [LANGUAGES.KYRGYZ]: "Kyrgyz",
  [LANGUAGES.LITHUANIAN]: "Lithuanian",
  [LANGUAGES.LATVIAN]: "Latvian",
  [LANGUAGES.MACEDONIAN]: "Macedonian",
  [LANGUAGES.MARATHI]: "Marathi",
  [LANGUAGES.MALAY]: "Malay",
  [LANGUAGES.NEPALI]: "Nepali",
  [LANGUAGES.DUTCH]: "Dutch",
  [LANGUAGES.NORWEGIAN]: "Norwegian",
  [LANGUAGES.PUNJABI]: "Punjabi",
  [LANGUAGES.POLISH]: "Polish",
  [LANGUAGES.PORTUGUESE]: "Portuguese",
  [LANGUAGES.ROMANIAN]: "Romanian",
  [LANGUAGES.RUSSIAN]: "Russian",
  [LANGUAGES.SLOVAK]: "Slovak",
  [LANGUAGES.SLOVENIAN]: "Slovenian",
  [LANGUAGES.ALBANIAN]: "Albanian",
  [LANGUAGES.SERBIAN]: "Serbian",
  [LANGUAGES.SWEDISH]: "Swedish",
  [LANGUAGES.SWAHILI]: "Swahili",
  [LANGUAGES.TAMIL]: "Tamil",
  [LANGUAGES.TELUGU]: "Telugu",
  [LANGUAGES.TAJIK]: "Tajik",
  [LANGUAGES.THAI]: "Thai",
  [LANGUAGES.TAGALOG]: "Tagalog",
  [LANGUAGES.TURKISH]: "Turkish",
  [LANGUAGES.URDU]: "Urdu",
  [LANGUAGES.UZBEK]: "Uzbek",
  [LANGUAGES.VIETNAMESE]: "Vietnamese",
  [LANGUAGES.YORUBA]: "Yoruba",
  [LANGUAGES.CHINESE]: "Chinese",
  [LANGUAGES.CHINESE_SIMPLIFIED]: "Chinese (Simplified)",
  [LANGUAGES.CHINESE_TRADITIONAL]: "Chinese (Traditional)",
} as const;

// Display language shortcodes mapping for i18n (complete set)
export const DISPLAY_LANGUAGE_CODES = {
  [LANGUAGES.AFRIKAANS]: "af",
  [LANGUAGES.AMHARIC]: "am",
  [LANGUAGES.ARABIC]: "ar",
  [LANGUAGES.BULGARIAN]: "bg",
  [LANGUAGES.BENGALI]: "bn",
  [LANGUAGES.BOSNIAN]: "bs",
  [LANGUAGES.CZECH]: "cs",
  [LANGUAGES.WELSH]: "cy",
  [LANGUAGES.DANISH]: "da",
  [LANGUAGES.GERMAN]: "de",
  [LANGUAGES.GREEK]: "el",
  [LANGUAGES.ENGLISH]: "en",
  [LANGUAGES.SPANISH]: "es",
  [LANGUAGES.ESTONIAN]: "et",
  [LANGUAGES.BASQUE]: "eu",
  [LANGUAGES.PERSIAN]: "fa",
  [LANGUAGES.FINNISH]: "fi",
  [LANGUAGES.FRENCH]: "fr",
  [LANGUAGES.IRISH]: "ga",
  [LANGUAGES.GAELIC]: "gd",
  [LANGUAGES.GUJARATI]: "gu",
  [LANGUAGES.HAUSA]: "ha",
  [LANGUAGES.HEBREW]: "he",
  [LANGUAGES.HINDI]: "hi",
  [LANGUAGES.CROATIAN]: "hr",
  [LANGUAGES.HUNGARIAN]: "hu",
  [LANGUAGES.INDONESIAN]: "id",
  [LANGUAGES.IGBO]: "ig",
  [LANGUAGES.ICELANDIC]: "is",
  [LANGUAGES.ITALIAN]: "it",
  [LANGUAGES.JAPANESE]: "ja",
  [LANGUAGES.KAZAKH]: "kk",
  [LANGUAGES.KOREAN]: "ko",
  [LANGUAGES.KYRGYZ]: "ky",
  [LANGUAGES.LITHUANIAN]: "lt",
  [LANGUAGES.LATVIAN]: "lv",
  [LANGUAGES.MACEDONIAN]: "mk",
  [LANGUAGES.MARATHI]: "mr",
  [LANGUAGES.MALAY]: "ms",
  [LANGUAGES.NEPALI]: "ne",
  [LANGUAGES.DUTCH]: "nl",
  [LANGUAGES.NORWEGIAN]: "no",
  [LANGUAGES.PUNJABI]: "pa",
  [LANGUAGES.POLISH]: "pl",
  [LANGUAGES.PORTUGUESE]: "pt",
  [LANGUAGES.ROMANIAN]: "ro",
  [LANGUAGES.RUSSIAN]: "ru",
  [LANGUAGES.SLOVAK]: "sk",
  [LANGUAGES.SLOVENIAN]: "sl",
  [LANGUAGES.ALBANIAN]: "sq",
  [LANGUAGES.SERBIAN]: "sr",
  [LANGUAGES.SWEDISH]: "sv",
  [LANGUAGES.SWAHILI]: "sw",
  [LANGUAGES.TAMIL]: "ta",
  [LANGUAGES.TELUGU]: "te",
  [LANGUAGES.TAJIK]: "tg",
  [LANGUAGES.THAI]: "th",
  [LANGUAGES.TAGALOG]: "tl",
  [LANGUAGES.TURKISH]: "tr",
  [LANGUAGES.URDU]: "ur",
  [LANGUAGES.UZBEK]: "uz",
  [LANGUAGES.VIETNAMESE]: "vi",
  [LANGUAGES.YORUBA]: "yo",
  [LANGUAGES.CHINESE]: "zh",
  [LANGUAGES.CHINESE_SIMPLIFIED]: "zh-CN",
  [LANGUAGES.CHINESE_TRADITIONAL]: "zh-TW",
} as const;

// Result languages - for tool output/content generation (expanded set)
export const RESULT_LANGUAGES = {
  [LANGUAGES.AFRIKAANS]: "Afrikaans",
  [LANGUAGES.ARABIC]: "Arabic",
  [LANGUAGES.BULGARIAN]: "Bulgarian",
  [LANGUAGES.BENGALI]: "Bengali",
  [LANGUAGES.CZECH]: "Czech",
  [LANGUAGES.DANISH]: "Danish",
  [LANGUAGES.GERMAN]: "German",
  [LANGUAGES.GREEK]: "Greek",
  [LANGUAGES.ENGLISH]: "English",
  [LANGUAGES.SPANISH]: "Spanish",
  [LANGUAGES.ESTONIAN]: "Estonian",
  [LANGUAGES.BASQUE]: "Basque",
  [LANGUAGES.PERSIAN]: "Farsi",
  [LANGUAGES.FINNISH]: "Finnish",
  [LANGUAGES.FRENCH]: "French",
  [LANGUAGES.IRISH]: "Irish",
  [LANGUAGES.GAELIC]: "Scottish Gaelic",
  [LANGUAGES.GUJARATI]: "Gujarati",
  [LANGUAGES.HINDI]: "Hindi",
  [LANGUAGES.CROATIAN]: "Croatian",
  [LANGUAGES.HUNGARIAN]: "Hungarian",
  [LANGUAGES.INDONESIAN]: "Indonesian",
  [LANGUAGES.ITALIAN]: "Italian",
  [LANGUAGES.JAPANESE]: "Japanese",
  [LANGUAGES.KOREAN]: "Korean",
  [LANGUAGES.LATVIAN]: "Latvian",
  [LANGUAGES.LITHUANIAN]: "Lithuanian",
  [LANGUAGES.MARATHI]: "Marathi",
  [LANGUAGES.MALAY]: "Malay",
  [LANGUAGES.NEPALI]: "Nepali",
  [LANGUAGES.DUTCH]: "Dutch",
  [LANGUAGES.NORWEGIAN]: "Norwegian",
  [LANGUAGES.PUNJABI]: "Punjabi",
  [LANGUAGES.POLISH]: "Polish",
  [LANGUAGES.PORTUGUESE]: "Portuguese",
  [LANGUAGES.ROMANIAN]: "Romanian",
  [LANGUAGES.RUSSIAN]: "Russian",
  [LANGUAGES.SLOVAK]: "Slovak",
  [LANGUAGES.SLOVENIAN]: "Slovenian",
  [LANGUAGES.SERBIAN]: "Serbian",
  [LANGUAGES.SWEDISH]: "Swedish",
  [LANGUAGES.SWAHILI]: "Swahili",
  [LANGUAGES.TAMIL]: "Tamil",
  [LANGUAGES.TELUGU]: "Telugu",
  [LANGUAGES.TAGALOG]: "Tagalog",
  [LANGUAGES.THAI]: "Thai",
  [LANGUAGES.TURKISH]: "Turkish",
  [LANGUAGES.URDU]: "Urdu",
  [LANGUAGES.VIETNAMESE]: "Vietnamese",
  [LANGUAGES.CHINESE]: "Chinese",
  [LANGUAGES.CHINESE_SIMPLIFIED]: "Chinese (Simplified)",
  [LANGUAGES.CHINESE_TRADITIONAL]: "Chinese (Traditional)",
  // Legacy entries for backwards compatibility
  [LANGUAGES.MANDARIN]: "Mandarin",
  [LANGUAGES.OTHER]: "Other",
} as const;

// Native language names mapping
export const NATIVE_LANGUAGE_NAMES = {
  [LANGUAGES.AFRIKAANS]: "Afrikaans",
  [LANGUAGES.AMHARIC]: "አማርኛ",
  [LANGUAGES.ARABIC]: "العربية",
  [LANGUAGES.BULGARIAN]: "Български",
  [LANGUAGES.BENGALI]: "বাংলা",
  [LANGUAGES.BOSNIAN]: "Bosanski",
  [LANGUAGES.CZECH]: "Čeština",
  [LANGUAGES.WELSH]: "Cymraeg",
  [LANGUAGES.DANISH]: "Dansk",
  [LANGUAGES.GERMAN]: "Deutsch",
  [LANGUAGES.GREEK]: "Ελληνικά",
  [LANGUAGES.ENGLISH]: "English",
  [LANGUAGES.SPANISH]: "Español",
  [LANGUAGES.ESTONIAN]: "Eesti",
  [LANGUAGES.BASQUE]: "Euskera",
  [LANGUAGES.PERSIAN]: "فارسی",
  [LANGUAGES.FINNISH]: "Suomi",
  [LANGUAGES.FRENCH]: "Français",
  [LANGUAGES.IRISH]: "Gaeilge",
  [LANGUAGES.GAELIC]: "Gàidhlig",
  [LANGUAGES.GUJARATI]: "ગુજરાતી",
  [LANGUAGES.HAUSA]: "Hausa",
  [LANGUAGES.HEBREW]: "עברית",
  [LANGUAGES.HINDI]: "हिन्दी",
  [LANGUAGES.CROATIAN]: "Hrvatski",
  [LANGUAGES.HUNGARIAN]: "Magyar",
  [LANGUAGES.INDONESIAN]: "Bahasa Indonesia",
  [LANGUAGES.IGBO]: "Igbo",
  [LANGUAGES.ICELANDIC]: "Íslenska",
  [LANGUAGES.ITALIAN]: "Italiano",
  [LANGUAGES.JAPANESE]: "日本語",
  [LANGUAGES.KAZAKH]: "Қазақ тілі",
  [LANGUAGES.KOREAN]: "한국어",
  [LANGUAGES.KYRGYZ]: "Кыргызча",
  [LANGUAGES.LITHUANIAN]: "Lietuvių",
  [LANGUAGES.LATVIAN]: "Latviešu",
  [LANGUAGES.MACEDONIAN]: "Македонски",
  [LANGUAGES.MARATHI]: "मराठी",
  [LANGUAGES.MALAY]: "Bahasa Melayu",
  [LANGUAGES.NEPALI]: "नेपाली",
  [LANGUAGES.DUTCH]: "Nederlands",
  [LANGUAGES.NORWEGIAN]: "Norsk",
  [LANGUAGES.PUNJABI]: "ਪੰਜਾਬੀ",
  [LANGUAGES.POLISH]: "Polski",
  [LANGUAGES.PORTUGUESE]: "Português",
  [LANGUAGES.ROMANIAN]: "Română",
  [LANGUAGES.RUSSIAN]: "Русский",
  [LANGUAGES.SLOVAK]: "Slovenčina",
  [LANGUAGES.SLOVENIAN]: "Slovenščina",
  [LANGUAGES.ALBANIAN]: "Shqip",
  [LANGUAGES.SERBIAN]: "Српски",
  [LANGUAGES.SWEDISH]: "Svenska",
  [LANGUAGES.SWAHILI]: "Kiswahili",
  [LANGUAGES.TAMIL]: "தமிழ்",
  [LANGUAGES.TELUGU]: "తెలుగు",
  [LANGUAGES.TAJIK]: "Тоҷикӣ",
  [LANGUAGES.THAI]: "ไทย",
  [LANGUAGES.TAGALOG]: "Tagalog",
  [LANGUAGES.TURKISH]: "Türkçe",
  [LANGUAGES.URDU]: "اردو",
  [LANGUAGES.UZBEK]: "O'zbek",
  [LANGUAGES.VIETNAMESE]: "Tiếng Việt",
  [LANGUAGES.YORUBA]: "Yorùbá",
  [LANGUAGES.CHINESE]: "中文",
  [LANGUAGES.CHINESE_SIMPLIFIED]: "简体中文",
  [LANGUAGES.CHINESE_TRADITIONAL]: "繁體中文",
  // Legacy entries
  [LANGUAGES.MANDARIN]: "官话",
  [LANGUAGES.OTHER]: "Other",
} as const;

export type DisplayLanguage = keyof typeof DISPLAY_LANGUAGES;
export type ResultLanguage = keyof typeof RESULT_LANGUAGES;

export const getDisplayLanguageEntries = () => Object.entries(DISPLAY_LANGUAGES);
export const getResultLanguageEntries = () => Object.entries(RESULT_LANGUAGES);
export const getDisplayLanguageCodes = () => Object.values(DISPLAY_LANGUAGE_CODES);

/**
 * Get the native name for a language when provided the English name
 * @param englishName - The English name of the language (e.g., "Spanish", "French")
 * @returns The native name of the language (e.g., "Español", "Français")
 */
export const getNativeLanguageName = (englishName: string): string => {
  // Find the language constant that matches the English name
  const languageKey = Object.keys(DISPLAY_LANGUAGES).find(
    key => DISPLAY_LANGUAGES[key as DisplayLanguage] === englishName
  ) as DisplayLanguage;

  if (languageKey && NATIVE_LANGUAGE_NAMES[languageKey]) {
    return NATIVE_LANGUAGE_NAMES[languageKey];
  }

  // Fallback to English name if not found
  return englishName;
};

/**
 * Get the native name for a language when provided the language constant
 * @param languageKey - The language constant (e.g., LANGUAGES.SPANISH)
 * @returns The native name of the language (e.g., "Español")
 */
export const getNativeLanguageNameByKey = (languageKey: DisplayLanguage): string => {
  return NATIVE_LANGUAGE_NAMES[languageKey] || DISPLAY_LANGUAGES[languageKey];
};

export const isDisplayLanguage = (lang: string): lang is DisplayLanguage => {
  return Object.values(LANGUAGES).includes(lang as LANGUAGES);
};

export const isResultLanguage = (lang: string): lang is ResultLanguage => {
  return Object.values(LANGUAGES).includes(lang as LANGUAGES);
};

export const DEFAULT_DISPLAY_LANGUAGE = LANGUAGES.ENGLISH;
export const DEFAULT_RESULT_LANGUAGE = LANGUAGES.ENGLISH;

/**
 * Normalize an incoming language value (may be shortcode like 'fr', locale like 'fr-FR',
 * already a constant value like 'french', or display name like 'French') to the
 * internal LANGUAGES enum value used by forms and API submissions.
 */
export const normalizeLanguageValue = (input?: string | null): LANGUAGES | string | undefined => {
  if (!input) return input || undefined;
  const lower = input.toLowerCase();

  // Already a LANGUAGES value
  if (Object.values(LANGUAGES).includes(lower as LANGUAGES)) return lower as LANGUAGES;

  // Try exact code match (e.g., 'fr')
  const entryByCode = Object.entries(DISPLAY_LANGUAGE_CODES).find(([, code]) => code.toLowerCase() === lower);
  if (entryByCode) return entryByCode[0] as LANGUAGES;

  // Try locale forms (e.g., fr-fr, fr_fr)
  const normalizedLocale = lower.replace('_', '-');
  const entryByLocale = Object.entries(DISPLAY_LANGUAGE_CODES).find(([, code]) => code.toLowerCase() === normalizedLocale);
  if (entryByLocale) return entryByLocale[0] as LANGUAGES;

  // Try display English name to enum value mapping (e.g., 'French' -> 'french')
  const displayMatch = Object.entries(DISPLAY_LANGUAGES).find(([, englishName]) => englishName.toLowerCase() === lower);
  if (displayMatch) return displayMatch[0] as LANGUAGES;

  return input; // fallback (unknown / custom)
};
